import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import AppTheme from "../shared-theme/AppTheme";
import { CustomCard, CustomContainer } from "../shared-theme/BasicComponentTheme";
import McdcLogo from "../../assets/images/logo-mcdc.png";
import ClemsonLogo from "../../assets/images/logo-clemson.png";
import Stack from "@mui/material/Stack";
import { Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuthManager } from "../../hooks/useAuthHook";

export default function EmailVerification(props: { disableCustomTheme?: boolean }) {
  const [unknownError, setUnknownError] = useState(false);
  const [verified, setVerified] = useState(false);
  const [expired, setExpired] = useState(false);

  const { useEmailVerify } = useAuthManager();
  const { mutate: emailVerify, isLoading } = useEmailVerify();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get("token") || "";

        const onSuccess = (response: any) => {
          if (response.code === 200) {
            setVerified(true);
            setTimeout(() => navigate("/signin"), 5000); // Redirect after 5 seconds
          }
        };

        const onError = () => {
          setExpired(true);
          setTimeout(() => navigate("/signin"), 5000); // Redirect after 5 seconds
        };

        emailVerify({ token }, { onSuccess, onError });
      } catch (error) {
        setUnknownError(true);
      }
    };

    handleAuthCallback();
  }, [emailVerify, navigate]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <CustomContainer direction="column" justifyContent="center">
        <CustomCard variant="outlined">
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 3,
            }}
          >
            <img
              src={McdcLogo}
              alt="MCDC Logo"
              style={{ maxWidth: "120px", maxHeight: "120px", objectFit: "contain" }}
            />
            <img
              src={ClemsonLogo}
              alt="Clemson Logo"
              style={{ maxWidth: "50px", maxHeight: "50px", objectFit: "contain" }}
            />
          </Box>

          {unknownError && (
            <Stack sx={{ width: "100%" }} spacing={2}>
              <Alert severity="error">
                An unexpected error occurred. Please let us know at{" "}
                <a
                  href="mailto:mcdcadmin@clemson.edu"
                  style={{ textDecoration: "none", color: "#1976d2", fontWeight: "bold" }}
                >
                  mcdcadmin@clemson.edu
                </a>.
              </Alert>
            </Stack>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%", gap: 2 }}>
            {isLoading && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Processing verification...
                </Typography>
              </Box>
            )}

            {verified && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Stack sx={{ width: "100%", mb: 3 }} spacing={2}>
                  <Alert severity="success">
                    Verification successful! Redirecting to sign-in in 5 seconds...
                  </Alert>
                </Stack>
              </Box>
            )}

            {expired && (
              <Box sx={{ textAlign: "center", mt: 3 }}>
                <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
                  <Alert severity="error">
                    Oops! This link is no longer valid or has expired. Please request a new one to proceed.
                  </Alert>
                  <Alert severity="info">
                    For assistance, contact us at{" "}
                    <a
                      href="mailto:mcdcadmin@clemson.edu"
                      style={{ textDecoration: "none", color: "#1976d2", fontWeight: "bold" }}
                    >
                      mcdcadmin@clemson.edu
                    </a>.
                  </Alert>
                </Stack>
                <Typography color="info" align="center">
                  Redirecting to sign-in in 5 seconds...
                </Typography>
              </Box>
            )}
          </Box>
        </CustomCard>
      </CustomContainer>
    </AppTheme>
  );
}
